// src/components/PazYSalvoStepWizard.jsx
import React from 'react';
import { CheckCircle, AlertCircle, AlertTriangle, XCircle, CheckSquare, Archive } from 'lucide-react';
import { Clock, FileText, Users } from './icons';

const stepConfig = {
    'Borrador': {
        icon: FileText,
        color: 'gray',
        label: 'Borrador',
        description: 'Documento creado, pendiente de envío a firma'
    },
    'EnFirma': {
        icon: Users,
        color: 'blue',
        label: 'En Firma',
        description: 'Esperando firmas de los responsables'
    },
    'Aprobado': {
        icon: CheckSquare,
        color: 'green',
        label: 'Aprobado',
        description: 'Todas las firmas completadas'
    },
    'PendienteFirmaGerenteGeneral': {
        icon: AlertCircle,
        color: 'orange',
        label: 'Pendiente Gerente General',
        description: 'Esperando firma final del Gerente General'
    },
    'Rechazado': {
        icon: XCircle,
        color: 'red',
        label: 'Rechazado',
        description: 'Documento rechazado por uno de los firmantes'
    },
    'ConRechazos': {
        icon: AlertTriangle,
        color: 'orange',
        label: 'Con Rechazos',
        description: 'Algunas firmas fueron rechazadas, proceso continúa'
    },
    'Cerrado': {
        icon: Archive,
        color: 'purple',
        label: 'Cerrado',
        description: 'Proceso completado y archivado'
    }
};

export default function PazYSalvoStepWizard({ pazYSalvo, firmas = [] }) {
    // Verificar si la empresa requiere firma de Gerente General
    const requiereGerenteGeneral = () => {
        if (!pazYSalvo?.usuarioEmpresa) return false;
        const empresa = pazYSalvo.usuarioEmpresa.toUpperCase();
        return empresa.includes('VICSA') || empresa.includes('B2B');
    };

    const getStepStatus = (step) => {
        if (!pazYSalvo) return 'pending';

        switch (step) {
            case 'Borrador':
                return pazYSalvo.estado === 'Borrador' ? 'current' : 'completed';
            case 'EnFirma':
                if (pazYSalvo.estado === 'EnFirma' || pazYSalvo.estado === 'ConRechazos') return 'current';
                if (['Aprobado', 'PendienteFirmaGerenteGeneral', 'Cerrado'].includes(pazYSalvo.estado)) return 'completed';
                return 'pending';
            case 'Aprobado':
                if (pazYSalvo.estado === 'Aprobado') return 'current';
                if (['PendienteFirmaGerenteGeneral', 'Cerrado'].includes(pazYSalvo.estado)) return 'completed';
                return 'pending';
            case 'PendienteFirmaGerenteGeneral':
                if (pazYSalvo.estado === 'PendienteFirmaGerenteGeneral') return 'current';
                if (pazYSalvo.estado === 'Cerrado') return 'completed';
                return 'pending';
            case 'Cerrado':
                return pazYSalvo.estado === 'Cerrado' ? 'completed' : 'pending';
            default:
                return 'pending';
        }
    };

    const getFirmasProgress = () => {
        if (!firmas || firmas.length === 0) return { completed: 0, total: 0 };

        const completed = firmas.filter(f => f.estado === 'Firmado').length;
        const total = firmas.length;

        return { completed, total };
    };

    const getFirmasStatus = () => {
        const { completed, total } = getFirmasProgress();

        if (completed === 0) return 'pending';
        if (completed === total) return 'completed';
        return 'current';
    };

    // Definir pasos dinámicamente según la empresa
    const getSteps = () => {
        const baseSteps = [
            { key: 'Borrador', label: 'Creado' },
            { key: 'EnFirma', label: 'En Firma' },
            { key: 'Aprobado', label: 'Aprobado' }
        ];

        // Solo agregar el paso de Gerente General si la empresa lo requiere
        if (requiereGerenteGeneral()) {
            baseSteps.push({ key: 'PendienteFirmaGerenteGeneral', label: 'Pendiente Gerente General' });
        }

        baseSteps.push({ key: 'Cerrado', label: 'Cerrado' });
        return baseSteps;
    };

    const steps = getSteps();

    const progress = getFirmasProgress();

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Progreso del Documento
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    Estado actual: <span className="font-medium">{pazYSalvo?.estado || 'N/A'}</span>
                </p>
            </div>

            {/* Progress Bar */}
            <div className="mb-8">
                <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                    {steps.map((step, index) => {
                        const stepInfo = stepConfig[step.key];
                        const status = getStepStatus(step.key);
                        const Icon = stepInfo.icon;
                        const isLast = index === steps.length - 1;

                        return (
                            <React.Fragment key={step.key}>
                                {/* Step Circle */}
                                <div className="flex flex-row sm:flex-col items-center w-full sm:w-auto px-4 sm:px-0">
                                    <div className={`
                                        w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-colors relative z-10 flex-shrink-0
                                    ${status === 'completed'
                                            ? 'bg-green-100 text-green-600 dark:bg-green-900/20 dark:text-green-400'
                                            : status === 'current'
                                                ? step === 'PendienteFirmaGerenteGeneral'
                                                    ? 'bg-orange-100 text-orange-600 dark:bg-orange-900/20 dark:text-orange-400'
                                                    : 'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
                                                : 'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500'
                                        }
                                    `}>
                                        <Icon className="w-6 h-6" />
                                    </div>
                                    <span className={`
                                        text-xs font-medium text-left sm:text-center ml-3 sm:ml-0 sm:mt-2 flex-1 sm:flex-none
                                        ${status === 'completed'
                                            ? 'text-green-600 dark:text-green-400'
                                            : status === 'current'
                                                ? step.key === 'PendienteFirmaGerenteGeneral'
                                                    ? 'text-orange-600 dark:text-orange-400'
                                                    : 'text-blue-600 dark:text-blue-400'
                                                : 'text-gray-400 dark:text-gray-500'
                                        }
                                    `}>
                                        {step.label}
                                    </span>
                                </div>

                                {/* Connecting Line - only if not last step */}
                                {!isLast && (
                                    <div className="flex-none sm:flex-1 flex flex-col sm:flex-row items-center justify-center w-full sm:w-auto h-8 sm:h-auto">
                                        <div className={`
                                            w-0.5 sm:w-full h-full sm:h-0.5 
                                            sm:mx-6 
                                            ${status === 'completed' || getStepStatus(steps[index + 1].key) === 'completed'
                                                ? 'bg-green-200 dark:bg-green-800'
                                                : 'bg-gray-200 dark:bg-gray-700'
                                            }
                                        `}
                                            style={{
                                                marginLeft: (window.innerWidth >= 640) ? (step.key === 'PendienteFirmaGerenteGeneral' ? '8px' : '24px') : '1.25rem',
                                                marginRight: (window.innerWidth >= 640) ? (steps[index + 1]?.key === 'PendienteFirmaGerenteGeneral' ? '8px' : '24px') : '0'
                                            }}
                                        />
                                    </div>
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>

            {/* Firmas Progress */}
            {pazYSalvo?.estado === 'EnFirma' && (
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-6">
                    <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-blue-900 dark:text-blue-400">
                            Progreso de Firmas
                        </h4>
                        <span className="text-sm text-blue-700 dark:text-blue-300">
                            {progress.completed} de {progress.total} firmas completadas
                        </span>
                    </div>

                    <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2">
                        <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${progress.total > 0 ? (progress.completed / progress.total) * 100 : 0}%` }}
                        />
                    </div>

                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                        {firmas.map((firma, index) => (
                            <div key={index} className="flex items-center space-x-2 text-sm">
                                <div className={`
                                    w-4 h-4 rounded-full flex items-center justify-center
                                    ${firma.estado === 'Firmado'
                                        ? 'bg-green-100 text-green-600 dark:bg-green-900/20 dark:text-green-400'
                                        : firma.estado === 'Rechazado'
                                            ? 'bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400'
                                            : 'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500'
                                    }
                                `}>
                                    {firma.estado === 'Firmado' ? (
                                        <CheckCircle className="w-3 h-3" />
                                    ) : firma.estado === 'Rechazado' ? (
                                        <XCircle className="w-3 h-3" />
                                    ) : (
                                        <Clock className="w-3 h-3" />
                                    )}
                                </div>
                                <span className={`
                                    ${firma.estado === 'Firmado'
                                        ? 'text-green-700 dark:text-green-300'
                                        : firma.estado === 'Rechazado'
                                            ? 'text-red-700 dark:text-red-300'
                                            : 'text-gray-600 dark:text-gray-400'
                                    }
                                `}>
                                    {firma.rol}
                                </span>
                                {firma.firmante && (
                                    <span className="text-gray-500 dark:text-gray-400 text-xs">
                                        - {firma.firmante.nombre} {firma.firmante.apellido}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Estado Actual */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                    {(() => {
                        const stepInfo = stepConfig[pazYSalvo?.estado] || stepConfig['Borrador'];
                        const Icon = stepInfo.icon;

                        return (
                            <div className={`
                                w-8 h-8 rounded-full flex items-center justify-center
                                ${stepInfo.color === 'green'
                                    ? 'bg-green-100 text-green-600 dark:bg-green-900/20 dark:text-green-400'
                                    : stepInfo.color === 'blue'
                                        ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
                                        : stepInfo.color === 'orange'
                                            ? 'bg-orange-100 text-orange-600 dark:bg-orange-900/20 dark:text-orange-400'
                                            : stepInfo.color === 'red'
                                                ? 'bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400'
                                                : stepInfo.color === 'purple'
                                                    ? 'bg-purple-100 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400'
                                                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                                }
                            `}>
                                <Icon className="w-4 h-4" />
                            </div>
                        );
                    })()}
                    <div>
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                            {stepConfig[pazYSalvo?.estado]?.label || 'Estado Desconocido'}
                        </h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            {stepConfig[pazYSalvo?.estado]?.description || 'Estado no reconocido'}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
